// Shared Cognito-group constants + group-based gate helpers.
//
// Every auth check on the frontend should derive from `cognito:groups`
// in the JWT id-token payload, NOT from a hardcoded username. Issue
// #143 sub-step 3 flipped `App.tsx` to this scheme via Q6's operator
// override; the post-merge review of MR !42 caught that the downstream
// gates (`canAccessApplicant`, the `AdminTasks` line in `pages/Applicant.tsx`,
// and seven sites in `components/NavBar.tsx`) still keyed off the legacy
// hardcoded usernames `admin-7w3bh8xp2ld4x` / `grader-r04w6p32dfvfn`. The
// net effect was that any real Admin in the Gen 2 Cognito pool whose
// username was NOT one of those two literals would be routed correctly
// to ViewerApp by App.tsx, then immediately denied access to every
// applicant detail page and shown the recommender NavBar UX -- the
// inverse of Q6's intent.
//
// This module is the single source of truth on the frontend for:
//
//   1. The Cognito group-name strings ("Admin", "Grader", "Recommender")
//      -- factored out so they live in one place. Same strings appear in
//      `amplify/auth/resource.ts` (the canonical group declaration),
//      `amplify/data/resource.ts` (`allow.groups([...])`),
//      `amplify/storage/resource.ts` (`allow.groups([...])`), and the
//      `amplify/data/custom-resolvers/*.vtl` files. Per AGENT.md's
//      magic-string guidance ("magic strings should live in `amplify/lib/`"
//      -- frontend-side analogue here), keeping a separate constants
//      module per layer is OK; what matters is that within `viewer-client/`
//      the literals don't get sprinkled across components.
//
//   2. The group-membership predicates (`isAdminOrGraderByGroups`,
//      `isAdminByGroups`, `isRecommenderByGroups`). These take the
//      `groups: string[]` derived from `fetchAuthSession()` rather than
//      a username, and are the only correct source-of-truth for a
//      "show admin UI?" / "deny access to this applicant?" decision.
//
//   3. A small React context (`AuthGroupsContext`) so descendants of
//      `App.tsx` (which is the one place where `fetchAuthSession()` is
//      called) can read the groups without re-fetching the session. The
//      provider lives in `App.tsx`; consumers use `useAuthGroups()`.
//
// What this module does NOT do:
//
//   - It does NOT call `fetchAuthSession()` itself. App.tsx owns that
//     side effect; this module is pure data + a typed context.
//   - It does NOT enforce group membership server-side. The VTL
//     resolvers (`amplify/data/custom-resolvers/Query.*.vtl`) and the
//     storage rules (`amplify/storage/resource.ts`) are the actual
//     authorization boundary. The frontend gates here are UX-only --
//     they decide which UI to render; they do NOT prevent a determined
//     user from issuing a direct AppSync call. See
//     `docs/integration-tests-pending.md` Phase 2.3 / 2.4 for what 2.9e
//     verifies.

import { createContext, useContext } from "react";

/**
 * The canonical Cognito group names. Match the strings declared in
 * `amplify/auth/resource.ts` (`groups: ["Admin", "Grader", "Recommender"]`).
 * If those strings ever change, update them here too -- the test suite
 * (#144) is the planned safety net.
 */
export const AUTH_GROUPS = {
  ADMIN: "Admin",
  GRADER: "Grader",
  RECOMMENDER: "Recommender",
} as const;

export type AuthGroupName =
  | typeof AUTH_GROUPS.ADMIN
  | typeof AUTH_GROUPS.GRADER
  | typeof AUTH_GROUPS.RECOMMENDER;

/**
 * `groups` is the raw `cognito:groups` claim, normalized to a string[]
 * by `extractGroups` in `App.tsx`. We accept the wide `string[]` type
 * (rather than `AuthGroupName[]`) because the JWT can in theory carry
 * arbitrary group memberships -- only the three canonical ones drive
 * the UX gates here.
 */
export const isAdminOrGraderByGroups = (groups: string[]): boolean =>
  groups.includes(AUTH_GROUPS.ADMIN) || groups.includes(AUTH_GROUPS.GRADER);

export const isAdminByGroups = (groups: string[]): boolean =>
  groups.includes(AUTH_GROUPS.ADMIN);

/**
 * "Recommender" is the lower-privilege role. We treat anyone who is NOT
 * in Admin or Grader as a recommender for UI purposes -- this matches
 * the pre-existing behavior of `utils.ts` `isRecommender(username)`,
 * which returned `true` for any username other than the two legacy
 * admin/grader literals. Membership in the explicit `Recommender`
 * group is not required (a user in no groups still sees the recommender
 * UX, falling closed if the session can't be read).
 *
 * If/when we want to require explicit `Recommender` group membership
 * (e.g., to lock out users with no group at all), change this to
 * `groups.includes(AUTH_GROUPS.RECOMMENDER)` and audit the App.tsx
 * routing branch that lands a stranger on TeacherView.
 */
export const isRecommenderByGroups = (groups: string[]): boolean =>
  !isAdminOrGraderByGroups(groups);

/**
 * React context for groups derived from the current session.
 *
 * Default value `[]` matches the pre-authentication shape -- a user who
 * has not signed in is treated as having no groups, so all admin gates
 * deny and all recommender gates pass. This is the same fail-closed
 * behavior `App.tsx`'s session-read error handler implements.
 *
 * `App.tsx` is the only `Provider` site; every page/component that
 * needs to gate UI consumes via `useAuthGroups()`.
 */
export const AuthGroupsContext = createContext<string[]>([]);

export const useAuthGroups = (): string[] => useContext(AuthGroupsContext);
